import * as api from '@codegrade/api';
import { assert, eitherAsync } from '@codegrade/utils';
import { getErrorMessage } from './error';
import { ifExpr } from './expr';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function emptyResponse() {
    return eitherAsync.rightFromValue(api.ApiResult.fromResponseAndValue(new Response(), null));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function responseWithValue(value) {
    return eitherAsync.rightFromValue(api.ApiResult.fromResponseAndValue(new Response(), value));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function errorResponse(message) {
    return eitherAsync.leftFromValue(new Error(message));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function loadWithMaybeForce(value, loader, force, load, makeDefault) {
    const loadIfForce = ifExpr(force, 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    () => () => load(), () => 
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    (dflt) => dflt());
    return value.caseOf({
        Just: justValue => loader.caseOf({
            Just: dflt => loadIfForce(() => dflt),
            Nothing: () => loadIfForce(() => makeDefault(justValue)),
        }),
        Nothing: () => {
            if (loader.isJust()) {
                return loader.value;
            }
            return load();
        },
    });
}
/**
 * Load some API data with `force` parameter respected.
 *
 * @param data The options to use, see the object for the meaning of each field.
 * @returns The loader for the data, which might be freshly created.
 */
export function loadApiWithMaybeForce(data) {
    /**
     * Start the loader, add it using `addLoader` and remove it after it is done using
     * `removeLoader`.
     *
     * @returns A promise that resolves to the result of the loader.
     */
    const addAndLoad = () => {
        const loader = data.load().finally(() => data.removeLoader());
        data.addLoader(loader);
        return loader;
    };
    return loadWithMaybeForce(data.value, data.loader, data.force, addAndLoad, value => Promise.resolve(api.ApiResult.fromResponseAndValue(new Response(), value)));
}
/**
 * Load some data with `force` parameter respected.
 *
 * This is different than `loadWithMaybeForce` because it idesn't require you to
 * use `ApiResults`.
 *
 * @param data The options to use, see the object for the meaning of each field.
 * @returns The loader for the data, which might be freshly created.
 */
export async function storeLoadWithMaybeForce(data) {
    /**
     * Start the loader, add it using `addLoader` and remove it after it is done using
     * `removeLoader`.
     *
     * @returns A promise that resolves to the result of the loader.
     */
    const addAndLoad = () => {
        const loader = data.load().finally(() => data.removeLoader());
        data.addLoader(loader);
        return loader;
    };
    return await loadWithMaybeForce(data.value, data.loader, data.force, addAndLoad, async (value) => value);
}
/**
 * Check if an error is a parsed API error (has a {@link api.BaseError} body).
 *
 * @param err The error to check.
 * @returns Whether the error is an API result error with a parsed body.
 */
export function isApiError(err) {
    return err instanceof api.ApiResultError && err.error instanceof api.BaseError;
}
/**
 * Check if an unknown error is an API error with a specific code.
 *
 * @param err The error to check.
 * @param code The API error code to match.
 * @returns Whether the error is an API error with the given code.
 */
export function isApiErrorWithCode(err, code) {
    return err instanceof api.ApiResultError && isApiError(err) && err.error.code === code;
}
/**
 * Run a bunch of `EitherAsync`s in the background and log the error if they reject.
 *
 * @param eitherAsyncs The `EitherAsync`s to run.
 */
export function loadBackgroundApis(...eitherAsyncs) {
    for (const val of eitherAsyncs) {
        val.ifLeft(err => {
            // eslint-disable-next-line
            console.error(new Error(getErrorMessage(err)), err);
        }).run();
    }
}
/**
 * Run a bunch of `Promise`s in the background, catches the errors without
 * throwing them.
 *
 * Only call this method from `created`, `mounted`, or watchers, other methods
 * should just return the promise.
 *
 * This function returns BEFORE the tasks have settled, this is by design.
 *
 * @param tasks The promises to attach error handlers to.
 */
export function loadBackgroundPromises(...tasks) {
    // We create the error here to have the stacktrace of the calling method.
    const baseErr = new Error('loadBackgroundPromises: Background promise failed');
    for (const task of tasks) {
        task.catch(err => {
            // eslint-disable-next-line no-console
            console.error(baseErr, err);
        });
    }
}
/**
 * Do store loading of a paginated response, possibly forcing a new load.
 *
 * @param root0 The parameters to use.
 * @returns The promise to return to the store client.
 */
export async function loadPaginatedWithForce({ getData, toLoad, load, commitData, }) {
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const curLoader = getData()?.pageLoader;
        if (curLoader == null) {
            break;
        }
        if (curLoader != null) {
            // eslint-disable-next-line no-await-in-loop
            await curLoader;
            switch (toLoad) {
                case 'first-page':
                case 'next-page':
                    return;
                case 'force':
                case 'force-all-pages':
                case 'all-pages':
                    break;
                default:
                    assert.AssertionError.assertNever(toLoad);
            }
        }
    }
    const beforeData = getData();
    let nextToken;
    if (toLoad !== 'force' && toLoad !== 'force-all-pages') {
        const pagination = beforeData?.pagination;
        switch (pagination?.tag) {
            case undefined:
            case 'not-started':
                break;
            case 'token':
                if (toLoad === 'first-page') {
                    return;
                }
                nextToken = pagination.token;
                break;
            case 'finished':
                return;
            default:
                assert.AssertionError.assertNever(pagination);
        }
    }
    const loader = (async () => {
        const response = await load(nextToken);
        const pagination = response.nextToken == null
            ? { tag: 'finished' }
            : {
                tag: 'token',
                token: response.nextToken,
            };
        const newData = response.data;
        let fullData;
        if (toLoad === 'force' || toLoad === 'force-all-pages' || nextToken == null) {
            // When loading the first page (no continuation token), replace
            // rather than concatenate. Concatenation only makes sense for
            // subsequent pages. This also guards against a race where two
            // first-page loads run concurrently and the second would otherwise
            // duplicate the first's data.
            fullData = newData;
        }
        else {
            const oldData = getData()?.data ?? [];
            fullData = oldData.concat(newData);
        }
        commitData({
            pagination,
            pageLoader: undefined,
            data: Object.freeze(fullData),
        });
        return response;
    })();
    commitData({
        pagination: { tag: 'not-started' },
        ...beforeData,
        pageLoader: loader.catch(err => {
            const curData = getData();
            commitData({
                pagination: curData?.pagination ?? { tag: 'not-started' },
                data: curData?.data,
                pageLoader: undefined,
            });
            throw err;
        }),
    });
    await loader;
    if (toLoad === 'all-pages' || toLoad === 'force-all-pages') {
        // Always move to all-pages as `force-all-pages` would clear the store again.
        await loadPaginatedWithForce({ commitData, getData, load, toLoad: 'all-pages' });
    }
}
/**
 * Generic function to get the current paginated state for assistants or conversations.
 *
 * @param paginatedState The specific paginated state for assistants or conversations.
 * @returns The current state of the data.
 */
export function getPaginatedState(paginatedState) {
    if (paginatedState == null) {
        return Object.freeze({ state: 'not-started', data: [] });
    }
    const { data = [], pageLoader, pagination } = paginatedState;
    if (pageLoader != null) {
        return Object.freeze({ state: 'loading', data });
    }
    return Object.freeze({
        state: {
            finished: 'loaded-all',
            'not-started': 'not-started',
            token: 'loaded',
        }[pagination.tag],
        data,
    });
}
/**
 * Load pages until overlap with existing data is found.
 *
 * @param existingState The current paginated state from the store.
 * @param getId Gets the ID and is used for overlap checking.
 * @param loadPage Loads a single page of data.
 * @returns The new paginated state to commit to the store.
 */
export async function loadMoreRecentPages(existingState, getId, loadPage) {
    const existingData = existingState?.data ?? [];
    // If we haven't loaded anything then load the first page and return.
    if (existingData.length === 0) {
        const result = await loadPage(undefined);
        return {
            pagination: result.nextToken == null
                ? { tag: 'finished' }
                : { tag: 'token', token: result.nextToken },
            data: result.data,
        };
    }
    const firstExistingId = getId(existingData[0]);
    let newData = [];
    let nextToken;
    do {
        // eslint-disable-next-line no-await-in-loop
        const result = await loadPage(nextToken);
        newData = newData.concat(result.data);
        if (result.data.some(item => getId(item) === firstExistingId)) {
            break;
        }
        nextToken = result.nextToken;
    } while (nextToken != null);
    const pagination = existingState?.pagination.tag === 'token' ? existingState.pagination : { tag: 'finished' };
    return {
        pagination,
        data: [...newData, ...existingData],
    };
}
